import { useEffect, useState } from "react";
import { Modal, Space, Tag, Typography, Empty } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { t } from "i18next";
import moment from "moment";
import ApiService from "../../core/service/ApiService";

const { Text, Paragraph } = Typography;

const codeBlockStyle = {
    backgroundColor: '#f5f5f5',
    padding: 12,
    borderRadius: 4,
    fontFamily: 'monospace',
    fontSize: 12,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    maxHeight: 220,
    overflow: 'auto'
}

export default function EnabizOperationDetailModal({ messageId, open, onClose }) {
    const [data, setData] = useState(null);

    useEffect(() => {
        if (!open || !messageId) {
            setData(null);
            return;
        }
        ApiService.AppointmentTeletip.GetHl7Message(messageId).then(res => {
            if (res.success) setData(res.data);
        })
    }, [open, messageId])

    return (
        <Modal
            title={t('HL7 Message Detail')}
            open={open}
            onCancel={onClose}
            footer={null}
            width={820}
        >
            {!messageId ? (
                <Empty description={t('No HL7 messages found')} />
            ) : !data ? null : (
                <div>
                    <div style={{ marginBottom: 12 }}>
                        <Space wrap>
                            <Text strong>{t('Operation')}:</Text>
                            <Text>{data.operationName}</Text>
                            {data.accessionNumber && <Text code>{data.accessionNumber}</Text>}
                        </Space>
                    </div>
                    <div style={{ marginBottom: 12 }}>
                        <Space>
                            <Text strong>{t('Type')}:</Text>
                            <Tag color={data.type === 1 ? 'blue' : 'green'}>{data.typeName}</Tag>
                            <Text strong>{t('Status')}:</Text>
                            {data.success
                                ? <Tag icon={<CheckCircleOutlined />} color="success">{t('Success')}</Tag>
                                : <Tag icon={<CloseCircleOutlined />} color="error">{t('Failed')}</Tag>}
                        </Space>
                    </div>

                    <div style={{ marginBottom: 12 }}>
                        <Text strong>{t('HL7 Message')}:</Text>
                        <Paragraph copyable style={codeBlockStyle}>{data.message}</Paragraph>
                    </div>

                    {data.ackMessage && (
                        <div style={{ marginBottom: 12 }}>
                            <Text strong>{t('ACK Response')}:</Text>
                            <Paragraph copyable style={{ ...codeBlockStyle, backgroundColor: '#f6ffed' }}>{data.ackMessage}</Paragraph>
                        </div>
                    )}

                    {data.error && (
                        <div style={{ marginBottom: 12 }}>
                            <Text strong type="danger">{t('Request Error')}:</Text>
                            <Paragraph style={{ ...codeBlockStyle, backgroundColor: '#fff2f0', border: '1px solid #ffccc7', color: '#cf1322' }}>
                                {data.error}
                            </Paragraph>
                        </div>
                    )}

                    {data.reportMessage && (
                        <>
                            <div style={{ marginTop: 18, marginBottom: 8, borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
                                <Space>
                                    <Text strong style={{ fontSize: 15 }}>{t('Report Message')}</Text>
                                    {data.reportSuccess
                                        ? <Tag icon={<CheckCircleOutlined />} color="success">{t('Sent')}</Tag>
                                        : data.reportSentAt
                                            ? <Tag icon={<CloseCircleOutlined />} color="error">{t('Failed')}</Tag>
                                            : <Tag>{t('Not Sent')}</Tag>}
                                </Space>
                            </div>
                            <div style={{ marginBottom: 12 }}>
                                <Paragraph copyable style={{ ...codeBlockStyle, backgroundColor: '#e6f7ff' }}>
                                    {data.reportMessage}
                                </Paragraph>
                            </div>
                            {data.reportAckMessage && (
                                <div style={{ marginBottom: 12 }}>
                                    <Text strong>{t('Report ACK Response')}:</Text>
                                    <Paragraph copyable style={{ ...codeBlockStyle, backgroundColor: '#f6ffed' }}>{data.reportAckMessage}</Paragraph>
                                </div>
                            )}
                            {data.reportError && (
                                <div style={{ marginBottom: 12 }}>
                                    <Text strong type="danger">{t('Report Error')}:</Text>
                                    <Paragraph style={{ ...codeBlockStyle, backgroundColor: '#fff2f0', border: '1px solid #ffccc7', color: '#cf1322' }}>
                                        {data.reportError}
                                    </Paragraph>
                                </div>
                            )}
                        </>
                    )}

                    <div style={{ marginTop: 8 }}>
                        <Space wrap>
                            <Text type="secondary">{t('Created')}: {moment(data.createdDate).format('DD.MM.YYYY HH:mm:ss')}</Text>
                            {data.sentAt && <Text type="secondary">{t('Request Sent')}: {moment(data.sentAt).format('DD.MM.YYYY HH:mm:ss')}</Text>}
                            {data.reportSentAt && <Text type="secondary">{t('Report Sent')}: {moment(data.reportSentAt).format('DD.MM.YYYY HH:mm:ss')}</Text>}
                        </Space>
                    </div>
                </div>
            )}
        </Modal>
    )
}
